import { useMemo } from "react";
import { Link as LinkIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SuggestedLinksProps {
  content: string;
  onInsert: (link: { text: string; url: string }) => void;
  currentArticleId?: string;
}

export function SuggestedLinks({ content, onInsert, currentArticleId }: SuggestedLinksProps) {
  const { data: articles } = useQuery({
    queryKey: ["suggested-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("artigos")
        .select("id, titulo, slug, palavra_chave, categorias_blog(slug)")
        .eq("status", "publicado")
        .neq("id", currentArticleId || "");
      if (error) throw error;
      return data;
    }
  });

  const suggestions = useMemo(() => {
    if (!content || !articles) return [];
    
    const plainText = content.toLowerCase();
    const result: any[] = [];
    
    // Check which articles already have links in content to avoid duplicates
    const linkedUrls = (content.match(/\[.*?\]\((.*?)\)/g) || [])
      .map(m => m.match(/\((.*?)\)/)?.[1] || "");

    for (const art of articles) {
      if (!art.palavra_chave) continue;
      
      const kw = art.palavra_chave.toLowerCase().trim();
      const url = `/guias/${art.categorias_blog?.slug || 'geral'}/${art.slug}`;
      
      if (linkedUrls.includes(url)) continue;

      // Simple keyword match
      if (plainText.includes(kw)) {
        result.push({
          term: art.palavra_chave,
          title: art.titulo,
          url: url
        });
      }
      
      if (result.length >= 5) break;
    }
    
    return result;
  }, [content, articles]);

  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-3 pt-4 border-t mt-4">
      <div className="flex items-center gap-2 text-xs font-bold text-[#0C1A12] uppercase tracking-wider">
        <LinkIcon className="w-3 h-3 text-primary" />
        Links internos sugeridos
      </div>
      <div className="space-y-2">
        {suggestions.map((s, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border text-[13px] group shadow-sm">
            <span className="text-gray-600 leading-relaxed pr-4">
              O termo <strong className="text-[#0C1A12]">"{s.term}"</strong> aparece no seu texto e existe um artigo sobre isso.
            </span>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8 text-[11px] hover:bg-primary hover:text-[#0C1A12] border-primary/20 shrink-0"
              onClick={() => onInsert({ text: s.term, url: s.url })}
            >
              <Plus className="w-3 h-3 mr-1" /> Inserir link
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}